import type { PropsWithChildren, ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Suspense } from '@sorare/core/src/atoms/Suspense';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { AppNavigation } from '@sorare/core/src/components/navigation/AppNavigation';
import { UserFlags } from '@sorare/core/src/components/user/UserFlags';
import {
  ACTIVITY,
  ANY_SPORT_ESSENCE_TYPE,
  ANY_SPORT_MARKET,
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  ANY_SPORT_PLAYERS_SLUG_CARDS,
  FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  FOOTBALL_TEAMS_SLUG_CARDS,
  MLB_TEAMS_SLUG_CARDS,
  NBA_TEAMS_SLUG_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useAutoSyncProMode } from '@sorare/core/src/hooks/commonGame/useAutoSyncProMode';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useTransparentAppHeader } from '@sorare/core/src/hooks/navigation/useTransparentAppHeader';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { MultiSportFooter } from '@sorare/core/src/routing/MultiSportFooter';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import { NewAppHeader } from '#src/components/navigation/NewAppHeader/index.tsx';

const wrapper = css`
  display: flex;
  min-height: var(--100vh);
  flex-direction: column-reverse;
  /* Stacking context so the shell-background portal's z-index: -1 layers above the
   * opaque body background instead of being hidden behind it. */
  isolation: isolate;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

const portalReceiver = css`
  width: 100%;
  position: sticky;
  bottom: var(--my-bids-banner-height, 0px);
  right: 0;
`;

// min-width: 0; used to override default min-width: content; Prevents content from overflowing window container (Firefox)
const mainWrapper = css`
  flex: 1;
  overflow: clip;
  min-width: 0;
`;

const content = css`
  isolation: isolate;
  position: relative;
`;

const pageBackgroundPortal = css`
  position: absolute;
  isolation: isolate;
  z-index: -1;
  inset: 0;
  pointer-events: none;
`;

const shellBackgroundPortal = css`
  position: fixed;
  isolation: isolate;
  z-index: -1;
  inset: 0;
  pointer-events: none;
`;

const FOOTBALL_MARKET = generateSportPath(ANY_SPORT_MARKET, {
  sport: Sport.FOOTBALL,
});

const MOBILE_PAGES_WITHOUT_FOOTER = [ANY_SPORT_ESSENCE_TYPE];

const ANY_SPORT_PAGES_WITHOUT_FOOTER = [
  ANY_SPORT_PLAYERS_SLUG_CARDS,
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
];

const PAGES_WITHOUT_FOOTER = [
  FOOTBALL_TEAMS_SLUG_CARDS,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS,
  NBA_TEAMS_SLUG_CARDS,
  MLB_TEAMS_SLUG_CARDS,
  ACTIVITY,
  (pathname: string) =>
    matchPath(catchAll(FOOTBALL_MARKET), pathname) &&
    !matchPath(FOOTBALL_MARKET, pathname),
];

const useDisplayFooter = () => {
  const { pathname } = useLocation();
  const { isIosApp } = useIsMobileApp();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { matchAnySportPath } = useSportContext();

  return (
    !isIosApp &&
    !ANY_SPORT_PAGES_WITHOUT_FOOTER.some(path =>
      matchAnySportPath(path, pathname)
    ) &&
    !PAGES_WITHOUT_FOOTER.some(path =>
      typeof path === 'string' ? matchPath(path, pathname) : path(pathname)
    ) &&
    (isTabletAndAbove ||
      MOBILE_PAGES_WITHOUT_FOOTER.every(
        path => !matchAnySportPath(path, pathname)
      ))
  );
};

export const AppLayout = ({
  children,
  extraHeaderButton,
}: PropsWithChildren<{ extraHeaderButton?: ReactNode }>) => {
  useAutoSyncProMode();
  const displayFooter = useDisplayFooter();
  const isLaptopAndAbove = useIsLaptop();
  const useTransparentHeader = useTransparentAppHeader();

  return (
    <div className={wrapper}>
      <div className={shellBackgroundPortal} id="shell-background" />
      <AppNavigation />
      <div className={mainWrapper}>
        <ContentWithStickyHeader
          header={<NewAppHeader extraHeaderButton={extraHeaderButton} />}
          transparentHeader={!!useTransparentHeader && !isLaptopAndAbove}
        >
          {!isLaptopAndAbove && (
            <div className={pageBackgroundPortal} id="page-background" />
          )}
          <main className={content}>
            <UserFlags />
            {!!isLaptopAndAbove && (
              <div className={pageBackgroundPortal} id="page-background" />
            )}
            <Suspense fallback={<LoadingIndicator fullHeight />}>
              {children}
            </Suspense>
          </main>
          {!!displayFooter && <MultiSportFooter />}
        </ContentWithStickyHeader>
        {!!isLaptopAndAbove && (
          <Vertical
            className={portalReceiver}
            gap={0}
            id="above-bottom-bar-portal"
          />
        )}
      </div>
    </div>
  );
};
